import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  MapPin,
  Calendar,
  DollarSign,
  Activity,
  Building,
  AlertTriangle,
  FileText,
  TrendingUp
} from 'lucide-react';
import { Work, WorkStage, GrantLevel, CategoryPST, ModalProps } from '../../types/work.types';
import { worksApiService } from '../../services/workApi.service';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const STAGES_API_BASE =
  process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

// Senior officers (EE/SE/CE/EIC + admin, incl. per-jurisdiction role variants) may verify progress.
const isSeniorRole = (role?: string | null): boolean => {
  const r = String(role || '');
  return ['system_admin', 'engineer_in_chief', 'chief_engineer', 'superintending_engineer', 'executive_engineer', 'deputy_executive_engineer'].includes(r) || /^(ce_|se_|ee_)/.test(r);
};

// Per-event-type styling for the activity timeline.
const TL_META: Record<string, { dot: string; badge: string }> = {
  CREATE: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700' },
  UPDATE: { dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700' },
  STAGE: { dot: 'bg-purple-500', badge: 'bg-purple-100 text-purple-700' },
  PROGRESS: { dot: 'bg-cyan-500', badge: 'bg-cyan-100 text-cyan-700' },
  VERIFIED: { dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  SANCTION: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700' },
  AGREEMENT: { dot: 'bg-indigo-500', badge: 'bg-indigo-100 text-indigo-700' },
  FUND: { dot: 'bg-teal-500', badge: 'bg-teal-100 text-teal-700' },
  INSPECTION: { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700' },
  DELETE: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
  RESTORE: { dot: 'bg-gray-500', badge: 'bg-gray-100 text-gray-700' },
  _default: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' },
};

interface WorkDetailsProps extends ModalProps {
  workId: number;
}

const emptyForm = {
  month: '', year: String(new Date().getFullYear()),
  length_completed_current_month: '', expenditure_current_month: '',
  abstract_stage_of_work: '', sub_stage_of_work: '', detailed_stage_of_work: '',
  detailed_remarks: '',
};

const emptyFund = {
  entry_type: 'ALLOCATION', amount: '', scheme: '', reference_no: '',
  status: '', sna_pfms_ref: '', entry_date: '', remarks: '',
};

const FUND_TYPES = ['ALLOCATION', 'RELEASE', 'RA_BILL', 'PAYMENT', 'RECOVERY', 'DEDUCTION'];

const emptyClosure = {
  stage: 'Under DLP', dlp_months: '', dlp_start_date: '', dlp_end_date: '',
  security_deposit_released: '', performance_guarantee_released: '',
  closure_date: '', closure_remarks: '',
};

const emptyInspection = {
  inspection_type: 'VQC', status: 'INSPECTED', scheduled_date: '', inspection_date: '',
  inspector_name: '', grade: '', defects_noted: '', remarks: '',
};
const INSPECTION_TYPES = ['VQC', 'PR'];
const INSPECTION_STATUS = ['SCHEDULED', 'INSPECTED', 'CANCELLED'];

const emptyBG = {
  bg_number: '', bg_amount: '', bg_expiry_date: '', bg_revalidated: '', bg_revalidated_date: '',
};

const emptySanction = {
  sanction_type: 'TS', sanction_number: '', sanction_date: '', sanction_amount: '',
  sanctioned_by: '', sanctioning_authority: '', remarks: '',
};
const SANCTION_TYPES = ['OAS', 'RAS', 'TS'];

const WorkDetails: React.FC<WorkDetailsProps> = ({ workId, isOpen, onClose }) => {
  const [work, setWork] = useState<Work | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(emptyForm);
  const [stageOpts, setStageOpts] = useState<{ abstract: string[]; sub: string[]; detailed: string[] }>({ abstract: [], sub: [], detailed: [] });
  const [funds, setFunds] = useState<any[]>([]);
  const [fundSummary, setFundSummary] = useState<any>(null);
  const [showFundForm, setShowFundForm] = useState(false);
  const [savingFund, setSavingFund] = useState(false);
  const [fundForm, setFundForm] = useState<Record<string, string>>(emptyFund);
  const [showClosureForm, setShowClosureForm] = useState(false);
  const [savingClosure, setSavingClosure] = useState(false);
  const [closureForm, setClosureForm] = useState<Record<string, string>>(emptyClosure);
  const [closureInfo, setClosureInfo] = useState<any>(null);
  const [authorities, setAuthorities] = useState<{ AS?: string | null; TS?: string | null; TENDER?: string | null } | null>(null);
  const [inspections, setInspections] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [savingInspection, setSavingInspection] = useState(false);
  const [inspectionForm, setInspectionForm] = useState<Record<string, string>>(emptyInspection);
  const [showBGForm, setShowBGForm] = useState(false);
  const [savingBG, setSavingBG] = useState(false);
  const [bgForm, setBgForm] = useState<Record<string, string>>(emptyBG);
  const [nextStage, setNextStage] = useState<{ next: { abstract: string; sub: string } | null; can_advance: boolean } | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { user } = useAuth();
  const canVerify = isSeniorRole((user as any)?.role);
  const [sanctions, setSanctions] = useState<any[]>([]);
  const [showSanctionForm, setShowSanctionForm] = useState(false);
  const [savingSanction, setSavingSanction] = useState(false);
  const [sanctionForm, setSanctionForm] = useState<Record<string, string>>(emptySanction);

  const fetchWorkDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, hist, fundRows, fundSum, closure, insp] = await Promise.all([
        worksApiService.getWorkById(workId),
        worksApiService.getProgressHistory(workId).catch(() => []),
        worksApiService.getFundHistory(workId).catch(() => []),
        worksApiService.getFundSummary(workId).catch(() => null),
        worksApiService.getClosure(workId).catch(() => null),
        worksApiService.getInspectionHistory(workId).catch(() => []),
      ]);
      setWork(data);
      setHistory(Array.isArray(hist) ? hist : []);
      setFunds(Array.isArray(fundRows) ? fundRows : []);
      setFundSummary(fundSum);
      setClosureInfo(closure);
      setInspections(Array.isArray(insp) ? insp : []);
      worksApiService.getNextStage(workId).then(setNextStage).catch(() => setNextStage(null));
      worksApiService.getSanctions(workId).then((s) => setSanctions(Array.isArray(s) ? s : [])).catch(() => setSanctions([]));
      worksApiService.getWorkTimeline(workId).then((t) => setTimeline(t?.events || [])).catch(() => setTimeline([]));

      // Resolve competent authority (delegation of powers) by this work's cost
      const cost = Number((data as any)?.estimate_cost) || 0;
      const scheme = (data as any)?.grant_type || 'ALL';
      const [asR, tsR, tenderR] = await Promise.all([
        worksApiService.resolveAuthority('AS', cost, scheme),
        worksApiService.resolveAuthority('TS', cost, scheme),
        worksApiService.resolveAuthority('TENDER_ACCEPTANCE', cost, scheme),
      ]);
      setAuthorities({ AS: asR?.authority, TS: tsR?.authority, TENDER: tenderR?.authority });

      // Load Stages-of-Work master scoped to this work's type for the dropdowns
      const wtId = (data as any)?.work_type_id;
      if (wtId) {
        try {
          const res = await fetch(`${STAGES_API_BASE}/work-stages/by-work-type/${wtId}`, {
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          });
          const rows: any[] = res.ok ? await res.json() : [];
          const byLevel = (lvl: string) => rows
            .filter((r) => r.stage_level === lvl)
            .sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0))
            .map((r) => r.name);
          setStageOpts({ abstract: byLevel('ABSTRACT'), sub: byLevel('SUB'), detailed: byLevel('DETAILED') });
        } catch {
          setStageOpts({ abstract: [], sub: [], detailed: [] });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work details');
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    if (isOpen) {
      fetchWorkDetails();
    }
  }, [isOpen, fetchWorkDetails]);

  const handleRecordProgress = async () => {
    if (!form.month || !form.year) {
      alert('Month and year are required');
      return;
    }
    try {
      setSaving(true);
      await worksApiService.recordProgress(workId, {
        month: Number(form.month),
        year: Number(form.year),
        length_completed_current_month: form.length_completed_current_month ? Number(form.length_completed_current_month) : 0,
        expenditure_current_month: form.expenditure_current_month ? Number(form.expenditure_current_month) : 0,
        abstract_stage_of_work: form.abstract_stage_of_work || undefined,
        sub_stage_of_work: form.sub_stage_of_work || undefined,
        detailed_stage_of_work: form.detailed_stage_of_work || undefined,
        detailed_remarks: form.detailed_remarks || undefined,
      });
      setForm(emptyForm);
      setShowForm(false);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to record progress');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!window.confirm('Verify the latest recorded progress for this work? This confirms it as an officer.')) return;
    try {
      setVerifying(true);
      await worksApiService.verifyProgress(workId);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to verify progress');
    } finally {
      setVerifying(false);
    }
  };

  const handleRecordFund = async () => {
    if (!fundForm.entry_type || !fundForm.amount) {
      alert('Entry type and amount are required');
      return;
    }
    try {
      setSavingFund(true);
      await worksApiService.recordFund(workId, {
        entry_type: fundForm.entry_type,
        amount: Number(fundForm.amount),
        scheme: fundForm.scheme || undefined,
        reference_no: fundForm.reference_no || undefined,
        status: fundForm.status || undefined,
        sna_pfms_ref: fundForm.sna_pfms_ref || undefined,
        entry_date: fundForm.entry_date || undefined,
        remarks: fundForm.remarks || undefined,
      });
      setFundForm(emptyFund);
      setShowFundForm(false);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to record fund');
    } finally {
      setSavingFund(false);
    }
  };

  const handleRecordClosure = async () => {
    try {
      setSavingClosure(true);
      await worksApiService.recordClosure(workId, {
        stage: closureForm.stage || undefined,
        dlp_months: closureForm.dlp_months ? Number(closureForm.dlp_months) : undefined,
        dlp_start_date: closureForm.dlp_start_date || undefined,
        dlp_end_date: closureForm.dlp_end_date || undefined,
        security_deposit_released: closureForm.security_deposit_released === 'yes',
        performance_guarantee_released: closureForm.performance_guarantee_released === 'yes',
        closure_date: closureForm.closure_date || undefined,
        closure_remarks: closureForm.closure_remarks || undefined,
      });
      setShowClosureForm(false);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to record closure');
    } finally {
      setSavingClosure(false);
    }
  };

  const handleRecordInspection = async () => {
    if (inspectionForm.status === 'INSPECTED' && !inspectionForm.inspection_date) {
      alert('Inspection date is required for an INSPECTED record');
      return;
    }
    try {
      setSavingInspection(true);
      await worksApiService.recordInspection(workId, {
        inspection_type: inspectionForm.inspection_type,
        status: inspectionForm.status,
        scheduled_date: inspectionForm.scheduled_date || undefined,
        inspection_date: inspectionForm.inspection_date || undefined,
        inspector_name: inspectionForm.inspector_name || undefined,
        grade: inspectionForm.grade || undefined,
        defects_noted: inspectionForm.defects_noted || undefined,
        remarks: inspectionForm.remarks || undefined,
      });
      setInspectionForm(emptyInspection);
      setShowInspectionForm(false);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to record inspection');
    } finally {
      setSavingInspection(false);
    }
  };

  const handleRecordBankGuarantee = async () => {
    if (!bgForm.bg_expiry_date && bgForm.bg_revalidated !== 'yes') {
      alert('Provide a BG expiry date, or mark it revalidated');
      return;
    }
    try {
      setSavingBG(true);
      await worksApiService.recordBankGuarantee(workId, {
        bg_number: bgForm.bg_number || undefined,
        bg_amount: bgForm.bg_amount ? Number(bgForm.bg_amount) : undefined,
        bg_expiry_date: bgForm.bg_expiry_date || undefined,
        bg_revalidated: bgForm.bg_revalidated === 'yes' ? true : (bgForm.bg_revalidated === 'no' ? false : undefined),
        bg_revalidated_date: bgForm.bg_revalidated_date || undefined,
      });
      setBgForm(emptyBG);
      setShowBGForm(false);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to record bank guarantee');
    } finally {
      setSavingBG(false);
    }
  };

  const handleRecordSanction = async () => {
    if (!sanctionForm.sanction_number || !sanctionForm.sanction_date) {
      alert('Sanction number and date are required');
      return;
    }
    try {
      setSavingSanction(true);
      await worksApiService.recordSanction(workId, {
        sanction_type: sanctionForm.sanction_type,
        sanction_number: sanctionForm.sanction_number,
        sanction_date: sanctionForm.sanction_date,
        sanction_amount: sanctionForm.sanction_amount ? Number(sanctionForm.sanction_amount) : 0,
        sanctioned_by: sanctionForm.sanctioned_by || undefined,
        sanctioning_authority: sanctionForm.sanctioning_authority || undefined,
        remarks: sanctionForm.remarks || undefined,
      });
      setSanctionForm(emptySanction);
      setShowSanctionForm(false);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to record sanction');
    } finally {
      setSavingSanction(false);
    }
  };

  const handleAdvanceStage = async () => {
    if (!nextStage?.next) return;
    if (!window.confirm(`Advance this work to "${nextStage.next.sub}"?`)) return;
    try {
      setAdvancing(true);
      await worksApiService.advanceStage(workId);
      await fetchWorkDetails();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to advance stage');
    } finally {
      setAdvancing(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">Loading work details...</p>
        </div>
      </div>
    );
  }

  if (error || !work) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Error</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500 mr-2" />
            <p className="text-red-600">{error || 'Work not found'}</p>
          </div>
          <button
            onClick={fetchWorkDetails}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const progress = worksApiService.calculateProgress(work);
  const delayed = worksApiService.isWorkDelayed(work);
  const stageColors = worksApiService.getStageColor(work.abstract_stage_of_work);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Work Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Work Information */}
        <div className="space-y-6">
          {/* Work Name and SNO */}
          <div>
            <h3 className="text-lg font-medium text-gray-900">{work.work_name}</h3>
            <p className="text-sm text-gray-600">SNO: {work.sno} {delayed && <AlertTriangle className="w-4 h-4 inline text-red-500 ml-2" />}</p>
          </div>

          {/* Agency and Grant Level */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <Building className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Agency</p>
                <p className="text-gray-900">{work.agency_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Grant Level</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  work.oas_grant_level === GrantLevel.CENTRAL 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {work.oas_grant_level}
                </span>
              </div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Estimated Cost</p>
                <p className="text-gray-900">{worksApiService.formatCurrency(work.estimate_cost)}</p>
              </div>
            </div>
            {work.ras_amount && work.ras_amount !== work.estimate_cost && (
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">RAS Amount</p>
                  <p className="text-gray-900">{worksApiService.formatCurrency(work.ras_amount)}</p>
                </div>
              </div>
            )}
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Expenditure</p>
                <p className="text-gray-900">{worksApiService.formatCurrency(work.cum_exp_on_work)}</p>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center">
            <MapPin className="w-5 h-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-gray-700">Location</p>
              <p className="text-gray-900">
                {work.district?.name || 'N/A'} {work.mandal?.name ? `, ${work.mandal.name}` : ''}
                {work.length_sanctioned_km && ` (${work.length_sanctioned_km} km)`}
              </p>
              {work.constituency?.name && (
                <p className="text-sm text-gray-600 mt-0.5">
                  Constituency: {work.constituency.name}
                  {work.constituency.code ? ` (${work.constituency.code})` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Progress */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Progress</p>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Financial Progress</span>
                  <span className="font-medium">{progress.financial}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.financial}%` }}
                  ></div>
                </div>
              </div>
              {work.length_sanctioned_km && work.length_sanctioned_km > 0 && (
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Physical Progress</span>
                    <span className="font-medium">{progress.physical}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress.physical}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stage and Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <TrendingUp className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Stage</p>
                <div className="space-y-1">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${stageColors.bg} ${stageColors.text}`}>
                    {work.abstract_stage_of_work}
                  </span>
                  {work.sub_stage_of_work && (
                    <p className="text-xs text-gray-600">{work.sub_stage_of_work}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Dates</p>
                <p className="text-gray-900">Sanctioned: {worksApiService.formatDate(work.oas_sanction_date)}</p>
                {work.completion_date_set && (
                  <p className={`text-sm ${delayed ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                    Due: {worksApiService.formatDate(work.completion_date_set)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Category */}
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-gray-700">Category</p>
              <p className="text-gray-900">
                {work.category === 'P' ? 'Plain' : work.category === 'S' ? 'SCP' : work.category === 'T' ? 'TSP' : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Ledger */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-purple-600" />
              Progress History
            </h3>
            <div className="flex items-center gap-2">
              {nextStage?.can_advance && nextStage.next && (
                <button
                  onClick={handleAdvanceStage}
                  disabled={advancing}
                  title={`Advance to "${nextStage.next.sub}"`}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {advancing ? 'Advancing…' : `Advance Stage → ${nextStage.next.sub}`}
                </button>
              )}
              {canVerify && history.length > 0 && (
                <button
                  onClick={handleVerify}
                  disabled={verifying}
                  title="Verify the latest recorded progress (officer confirmation)"
                  className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {verifying ? 'Verifying…' : '✓ Verify Latest'}
                </button>
              )}
              <button
                onClick={() => setShowForm((s) => !s)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                {showForm ? 'Cancel' : '+ Record Progress'}
              </button>
            </div>
          </div>

          {showForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <input className="border rounded px-2 py-1.5 text-sm" type="number" min={1} max={12} placeholder="Month (1-12)*"
                value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="number" placeholder="Year*"
                value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
              <select className="border rounded px-2 py-1.5 text-sm"
                value={form.abstract_stage_of_work} onChange={(e) => setForm({ ...form, abstract_stage_of_work: e.target.value })}>
                <option value="">Abstract stage…</option>
                {(stageOpts.abstract.length ? stageOpts.abstract : Object.values(WorkStage)).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="border rounded px-2 py-1.5 text-sm" type="number" step="0.001" placeholder="Length this period (km)"
                value={form.length_completed_current_month} onChange={(e) => setForm({ ...form, length_completed_current_month: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="Expenditure this period (Lakhs)"
                value={form.expenditure_current_month} onChange={(e) => setForm({ ...form, expenditure_current_month: e.target.value })} />
              <select className="border rounded px-2 py-1.5 text-sm"
                value={form.sub_stage_of_work} onChange={(e) => setForm({ ...form, sub_stage_of_work: e.target.value })}>
                <option value="">Sub stage…</option>
                {stageOpts.sub.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {stageOpts.detailed.length > 0 ? (
                <select className="border rounded px-2 py-1.5 text-sm md:col-span-2"
                  value={form.detailed_stage_of_work} onChange={(e) => setForm({ ...form, detailed_stage_of_work: e.target.value })}>
                  <option value="">Detailed stage…</option>
                  {stageOpts.detailed.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input className="border rounded px-2 py-1.5 text-sm md:col-span-2" type="text" placeholder="Detailed stage"
                  value={form.detailed_stage_of_work} onChange={(e) => setForm({ ...form, detailed_stage_of_work: e.target.value })} />
              )}
              <input className="border rounded px-2 py-1.5 text-sm md:col-span-3" type="text" placeholder="Remarks"
                value={form.detailed_remarks} onChange={(e) => setForm({ ...form, detailed_remarks: e.target.value })} />
              <div className="md:col-span-3 flex justify-end">
                <button disabled={saving} onClick={handleRecordProgress}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Progress'}
                </button>
              </div>
            </div>
          )}

          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No progress recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Period</th>
                    <th className="px-3 py-2 text-right">Len (period / cum)</th>
                    <th className="px-3 py-2 text-right">Exp (period / cum)</th>
                    <th className="px-3 py-2 text-left">Stage</th>
                    <th className="px-3 py-2 text-left">Remarks</th>
                    <th className="px-3 py-2 text-center">Verified</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td className="px-3 py-2">{String(h.month).padStart(2, '0')}/{h.year} <span className="text-gray-400">({h.financial_year})</span></td>
                      <td className="px-3 py-2 text-right">{Number(h.length_completed_current_month) || 0} / <b>{Number(h.cumulative_length_completed) || 0}</b></td>
                      <td className="px-3 py-2 text-right">{Number(h.expenditure_current_month) || 0} / <b>{Number(h.cumulative_expenditure) || 0}</b></td>
                      <td className="px-3 py-2">{h.current_stage || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{h.detailed_remarks || '-'}</td>
                      <td className="px-3 py-2 text-center">
                        {h.verified_by
                          ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium" title={h.verified_date ? `Verified ${new Date(h.verified_date).toLocaleDateString()}` : 'Verified'}>✓ Verified</span>
                          : <span className="text-gray-400 text-xs">Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity Timeline — every change, stage move, progress, verify & sub-entity event */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
            <Activity className="w-5 h-5 mr-2 text-blue-600" /> Activity Timeline
            <span className="ml-2 text-xs font-normal text-gray-500">({timeline.length} events)</span>
          </h3>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">No activity recorded yet.</p>
          ) : (
            <ol className="relative border-l-2 border-gray-200 ml-3 space-y-5">
              {timeline.map((e, i) => {
                const meta = TL_META[e.type] || TL_META._default;
                return (
                  <li key={i} className="ml-5">
                    <span className={`absolute -left-[9px] w-4 h-4 rounded-full ring-4 ring-white ${meta.dot}`} />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${meta.badge}`}>{e.type}</span>
                      <span className="text-sm font-medium text-gray-800">{e.title}</span>
                      <span className="text-xs text-gray-400">· {new Date(e.at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      by <span className="font-medium text-gray-700">{e.actor_name}</span>{e.detail ? ` · ${e.detail}` : ''}
                    </div>
                    {Array.isArray(e.changes) && e.changes.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {e.changes.slice(0, 8).map((c: any, j: number) => (
                          <span key={j} className="text-[11px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-gray-600">
                            {c.field}: <span className="text-gray-400">{c.from ?? '—'}</span> → <span className="text-gray-800">{String(c.to ?? '—')}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Sanctioning Authority (delegation of powers) */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center mb-3">
            <Building className="w-5 h-5 mr-2 text-amber-600" />
            Sanctioning Authority
            <span className="ml-2 text-xs font-normal text-gray-500">
              (by cost {worksApiService.formatCurrency(work.estimate_cost)}{work.grant_type ? ` · ${work.grant_type}` : ''})
            </span>
          </h3>
          {authorities ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {[['Administrative Sanction', authorities.AS], ['Technical Sanction', authorities.TS], ['Tender Acceptance', authorities.TENDER]].map(([label, who]) => (
                <div key={label as string} className="bg-amber-50 rounded-lg p-3">
                  <div className="text-gray-500 text-xs">{label}</div>
                  <div className="font-semibold text-gray-900">{(who as string) || 'Not configured'}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Resolving…</p>
          )}
        </div>

        {/* Fund Track */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              Fund Track
            </h3>
            <button onClick={() => setShowFundForm((s) => !s)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              {showFundForm ? 'Cancel' : '+ Record Fund'}
            </button>
          </div>

          {fundSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-sm">
              {[['Allocated', fundSummary.allocated], ['Released', fundSummary.released], ['RA-billed', fundSummary.ra_billed], ['Paid', fundSummary.paid], ['Unpaid bal.', fundSummary.unpaid_balance]].map(([k, v]) => (
                <div key={k as string} className="bg-gray-50 rounded p-2 text-center">
                  <div className="text-gray-500">{k}</div>
                  <div className="font-semibold text-gray-900">{Number(v) || 0}</div>
                </div>
              ))}
            </div>
          )}

          {showFundForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <select className="border rounded px-2 py-1.5 text-sm" value={fundForm.entry_type}
                onChange={(e) => setFundForm({ ...fundForm, entry_type: e.target.value })}>
                {FUND_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className="border rounded px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="Amount (Lakhs)*"
                value={fundForm.amount} onChange={(e) => setFundForm({ ...fundForm, amount: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="date" placeholder="Date"
                value={fundForm.entry_date} onChange={(e) => setFundForm({ ...fundForm, entry_date: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Reference (allotment/RA bill/UTR)"
                value={fundForm.reference_no} onChange={(e) => setFundForm({ ...fundForm, reference_no: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Status (Allotted/Sent to PFMS/Paid)"
                value={fundForm.status} onChange={(e) => setFundForm({ ...fundForm, status: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="SNA/PFMS ref"
                value={fundForm.sna_pfms_ref} onChange={(e) => setFundForm({ ...fundForm, sna_pfms_ref: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm md:col-span-3" type="text" placeholder="Remarks"
                value={fundForm.remarks} onChange={(e) => setFundForm({ ...fundForm, remarks: e.target.value })} />
              <div className="md:col-span-3 flex justify-end">
                <button disabled={savingFund} onClick={handleRecordFund}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  {savingFund ? 'Saving…' : 'Save Fund Entry'}
                </button>
              </div>
            </div>
          )}

          {funds.length === 0 ? (
            <p className="text-sm text-gray-500">No fund entries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {funds.map((f) => (
                    <tr key={f.id}>
                      <td className="px-3 py-2">{f.entry_date || '-'}</td>
                      <td className="px-3 py-2">{f.entry_type}</td>
                      <td className="px-3 py-2 text-right">{Number(f.amount) || 0}</td>
                      <td className="px-3 py-2">{f.reference_no || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{f.status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* DLP / Closure */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-indigo-600" />
              DLP / Closure
            </h3>
            <button onClick={() => setShowClosureForm((s) => !s)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              {showClosureForm ? 'Cancel' : 'Record DLP / Closure'}
            </button>
          </div>

          {showClosureForm ? (
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <select className="border rounded px-2 py-1.5 text-sm" value={closureForm.stage}
                onChange={(e) => setClosureForm({ ...closureForm, stage: e.target.value })}>
                <option value="Under DLP">Under DLP</option>
                <option value="Closed">Closed</option>
              </select>
              <input className="border rounded px-2 py-1.5 text-sm" type="number" placeholder="DLP months"
                value={closureForm.dlp_months} onChange={(e) => setClosureForm({ ...closureForm, dlp_months: e.target.value })} />
              <div />
              <label className="text-sm text-gray-700">DLP start
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={closureForm.dlp_start_date} onChange={(e) => setClosureForm({ ...closureForm, dlp_start_date: e.target.value })} /></label>
              <label className="text-sm text-gray-700">DLP end
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={closureForm.dlp_end_date} onChange={(e) => setClosureForm({ ...closureForm, dlp_end_date: e.target.value })} /></label>
              <label className="text-sm text-gray-700">Closure date
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={closureForm.closure_date} onChange={(e) => setClosureForm({ ...closureForm, closure_date: e.target.value })} /></label>
              <select className="border rounded px-2 py-1.5 text-sm" value={closureForm.security_deposit_released}
                onChange={(e) => setClosureForm({ ...closureForm, security_deposit_released: e.target.value })}>
                <option value="">Security deposit released?</option><option value="yes">Yes</option><option value="no">No</option>
              </select>
              <select className="border rounded px-2 py-1.5 text-sm" value={closureForm.performance_guarantee_released}
                onChange={(e) => setClosureForm({ ...closureForm, performance_guarantee_released: e.target.value })}>
                <option value="">Performance guarantee released?</option><option value="yes">Yes</option><option value="no">No</option>
              </select>
              <input className="border rounded px-2 py-1.5 text-sm md:col-span-3" type="text" placeholder="Closure remarks"
                value={closureForm.closure_remarks} onChange={(e) => setClosureForm({ ...closureForm, closure_remarks: e.target.value })} />
              <div className="md:col-span-3 flex justify-end">
                <button disabled={savingClosure} onClick={handleRecordClosure}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  {savingClosure ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-700 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><span className="text-gray-500">DLP:</span> {closureInfo?.dlp_months ? `${closureInfo.dlp_months} mo` : '—'}</div>
              <div><span className="text-gray-500">DLP end:</span> {closureInfo?.dlp_end_date || '—'}</div>
              <div><span className="text-gray-500">PG released:</span> {closureInfo?.performance_guarantee_released ? 'Yes' : 'No'}</div>
              <div><span className="text-gray-500">Closed:</span> {closureInfo?.closure_date || '—'}</div>
            </div>
          )}
        </div>

        {/* Sanctions (OAS / RAS / TS) */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Sanctions (OAS / RAS / TS)
            </h3>
            <button onClick={() => setShowSanctionForm((s) => !s)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              {showSanctionForm ? 'Cancel' : '+ Record Sanction'}
            </button>
          </div>

          {showSanctionForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <select className="border rounded px-2 py-1.5 text-sm" value={sanctionForm.sanction_type}
                onChange={(e) => setSanctionForm({ ...sanctionForm, sanction_type: e.target.value })}>
                {SANCTION_TYPES.map((t) => <option key={t} value={t}>{t === 'OAS' ? 'OAS (Administrative)' : t === 'RAS' ? 'RAS (Revised)' : 'TS (Technical)'}</option>)}
              </select>
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Proceedings / sanction no*"
                value={sanctionForm.sanction_number} onChange={(e) => setSanctionForm({ ...sanctionForm, sanction_number: e.target.value })} />
              <label className="text-sm text-gray-700">Sanction date*
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={sanctionForm.sanction_date} onChange={(e) => setSanctionForm({ ...sanctionForm, sanction_date: e.target.value })} /></label>
              <input className="border rounded px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="Amount (Lakhs)"
                value={sanctionForm.sanction_amount} onChange={(e) => setSanctionForm({ ...sanctionForm, sanction_amount: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Sanctioned by"
                value={sanctionForm.sanctioned_by} onChange={(e) => setSanctionForm({ ...sanctionForm, sanctioned_by: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Sanctioning authority"
                value={sanctionForm.sanctioning_authority} onChange={(e) => setSanctionForm({ ...sanctionForm, sanctioning_authority: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm md:col-span-3" type="text" placeholder="Remarks"
                value={sanctionForm.remarks} onChange={(e) => setSanctionForm({ ...sanctionForm, remarks: e.target.value })} />
              <div className="md:col-span-3 flex justify-end">
                <button disabled={savingSanction} onClick={handleRecordSanction}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  {savingSanction ? 'Saving…' : 'Save Sanction'}
                </button>
              </div>
            </div>
          )}

          {sanctions.length === 0 ? (
            <p className="text-sm text-gray-500">No sanctions recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Proceedings No</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Authority</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sanctions.map((s) => (
                    <tr key={s.id}>
                      <td className="px-3 py-2 font-medium">{s.sanction_type}</td>
                      <td className="px-3 py-2">{s.sanction_number || '-'}</td>
                      <td className="px-3 py-2">{s.sanction_date || '-'}</td>
                      <td className="px-3 py-2 text-right">{Number(s.sanction_amount) || 0}</td>
                      <td className="px-3 py-2 text-gray-600">{s.sanctioning_authority || s.sanctioned_by || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* QC Inspections */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-teal-600" />
              QC Inspections (V&amp;QC / PR)
            </h3>
            <button onClick={() => setShowInspectionForm((s) => !s)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              {showInspectionForm ? 'Cancel' : '+ Record Inspection'}
            </button>
          </div>

          {showInspectionForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <select className="border rounded px-2 py-1.5 text-sm" value={inspectionForm.inspection_type}
                onChange={(e) => setInspectionForm({ ...inspectionForm, inspection_type: e.target.value })}>
                {INSPECTION_TYPES.map((t) => <option key={t} value={t}>{t === 'VQC' ? 'V&QC' : 'PR'}</option>)}
              </select>
              <select className="border rounded px-2 py-1.5 text-sm" value={inspectionForm.status}
                onChange={(e) => setInspectionForm({ ...inspectionForm, status: e.target.value })}>
                {INSPECTION_STATUS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Inspector name"
                value={inspectionForm.inspector_name} onChange={(e) => setInspectionForm({ ...inspectionForm, inspector_name: e.target.value })} />
              <label className="text-sm text-gray-700">Scheduled
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={inspectionForm.scheduled_date} onChange={(e) => setInspectionForm({ ...inspectionForm, scheduled_date: e.target.value })} /></label>
              <label className="text-sm text-gray-700">Inspected on
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={inspectionForm.inspection_date} onChange={(e) => setInspectionForm({ ...inspectionForm, inspection_date: e.target.value })} /></label>
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="Grade (Good/Satisfactory/Poor)"
                value={inspectionForm.grade} onChange={(e) => setInspectionForm({ ...inspectionForm, grade: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm md:col-span-3" type="text" placeholder="Defects noted / remarks"
                value={inspectionForm.remarks} onChange={(e) => setInspectionForm({ ...inspectionForm, remarks: e.target.value })} />
              <div className="md:col-span-3 flex justify-end">
                <button disabled={savingInspection} onClick={handleRecordInspection}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  {savingInspection ? 'Saving…' : 'Save Inspection'}
                </button>
              </div>
            </div>
          )}

          {inspections.length === 0 ? (
            <p className="text-sm text-gray-500">No inspections recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Wing</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Inspector</th>
                    <th className="px-3 py-2 text-left">Grade</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {inspections.map((i) => (
                    <tr key={i.id}>
                      <td className="px-3 py-2">{i.inspection_date || i.scheduled_date || '-'}</td>
                      <td className="px-3 py-2">{i.inspection_type === 'VQC' ? 'V&QC' : 'PR'}</td>
                      <td className="px-3 py-2">{i.status}</td>
                      <td className="px-3 py-2">{i.inspector_name || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{i.grade || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bank Guarantee */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-amber-600" />
              Bank Guarantee
            </h3>
            <button onClick={() => setShowBGForm((s) => !s)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              {showBGForm ? 'Cancel' : 'Record / Revalidate BG'}
            </button>
          </div>

          {showBGForm ? (
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <input className="border rounded px-2 py-1.5 text-sm" type="text" placeholder="BG number"
                value={bgForm.bg_number} onChange={(e) => setBgForm({ ...bgForm, bg_number: e.target.value })} />
              <input className="border rounded px-2 py-1.5 text-sm" type="number" step="0.01" placeholder="BG amount"
                value={bgForm.bg_amount} onChange={(e) => setBgForm({ ...bgForm, bg_amount: e.target.value })} />
              <label className="text-sm text-gray-700">Expiry date
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={bgForm.bg_expiry_date} onChange={(e) => setBgForm({ ...bgForm, bg_expiry_date: e.target.value })} /></label>
              <select className="border rounded px-2 py-1.5 text-sm" value={bgForm.bg_revalidated}
                onChange={(e) => setBgForm({ ...bgForm, bg_revalidated: e.target.value })}>
                <option value="">Revalidated?</option><option value="yes">Yes</option><option value="no">No</option>
              </select>
              <label className="text-sm text-gray-700">Revalidated on
                <input className="border rounded px-2 py-1.5 text-sm w-full" type="date"
                  value={bgForm.bg_revalidated_date} onChange={(e) => setBgForm({ ...bgForm, bg_revalidated_date: e.target.value })} /></label>
              <div className="md:col-span-3 flex justify-end">
                <button disabled={savingBG} onClick={handleRecordBankGuarantee}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                  {savingBG ? 'Saving…' : 'Save BG'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Record the Bank Guarantee expiry to track it on the BGs-revalidations tile, or mark it revalidated to clear it.</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkDetails;